import React, { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '../utils/logger';
import {
  getAllCompaniesForFeatureGroup,
  createCompany,
  updateCompany,
  deleteCompany,
  getCompanyUsers,
  getDimensions,
  getTagsByDimension,
  updateCompanyBuyFeatureSettings
} from '../services/featureService';
import './Companies.css';

const Companies = ({ featureGroupId }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [votesPerPoll, setVotesPerPoll] = useState(1);
  const [companyWeight, setCompanyWeight] = useState('');
  const [companyType, setCompanyType] = useState('');
  const [companyAdminId, setCompanyAdminId] = useState('');
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [dimensions, setDimensions] = useState([]);
  const [tagsForDimension, setTagsForDimension] = useState([]);
  const [loadingDimensions, setLoadingDimensions] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [buyFeatureFormEnabled, setBuyFeatureFormEnabled] = useState(false);
  const [buyFeatureDimensionId, setBuyFeatureDimensionId] = useState('');
  const [buyFeatureTagId, setBuyFeatureTagId] = useState('');
  const [copiedBuyFeatureSlug, setCopiedBuyFeatureSlug] = useState(null);
  const copyFeedbackTimeoutRef = useRef(null);

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllCompaniesForFeatureGroup(featureGroupId);
      setCompanies(data);
    } catch (err) {
      setError('Failed to load companies');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [featureGroupId]);

  useEffect(() => {
    if (featureGroupId) {
      loadCompanies();
    }
  }, [featureGroupId, loadCompanies]);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showAddModal || !featureGroupId) return;
    let cancelled = false;
    (async () => {
      setLoadingDimensions(true);
      try {
        const dims = await getDimensions(featureGroupId);
        if (!cancelled) {
          setDimensions(dims || []);
        }
      } catch (err) {
        logger.error('Error loading dimensions:', err);
        if (!cancelled) setDimensions([]);
      } finally {
        if (!cancelled) setLoadingDimensions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAddModal, featureGroupId]);

  useEffect(() => {
    if (!showAddModal || !buyFeatureDimensionId) {
      setTagsForDimension([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingTags(true);
      try {
        const tags = await getTagsByDimension(buyFeatureDimensionId);
        if (!cancelled) setTagsForDimension(tags || []);
      } catch (err) {
        logger.error('Error loading tags:', err);
        if (!cancelled) setTagsForDimension([]);
      } finally {
        if (!cancelled) setLoadingTags(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAddModal, buyFeatureDimensionId]);

  const handleAdd = () => {
    setEditingCompany(null);
    setCompanyName('');
    setVotesPerPoll(1);
    setCompanyWeight('');
    setCompanyType('');
    setCompanyAdminId('');
    setCompanyUsers([]);
    setBuyFeatureFormEnabled(false);
    setBuyFeatureDimensionId('');
    setBuyFeatureTagId('');
    setTagsForDimension([]);
    setErrors({});
    setShowAddModal(true);
  };

  const handleEdit = async (company) => {
    setEditingCompany(company);
    setCompanyName(company.name);
    setVotesPerPoll(company.votes_per_poll || 1);
    setCompanyWeight(company.company_weight || '');
    setCompanyType(company.company_type || '');
    setCompanyAdminId(company.company_admin_id || '');
    const hasBuy = !!(company.buy_feature_slug && company.buy_feature_dimension_id && company.buy_feature_tag_id);
    setBuyFeatureFormEnabled(hasBuy);
    setBuyFeatureDimensionId(company.buy_feature_dimension_id || '');
    setBuyFeatureTagId(company.buy_feature_tag_id || '');
    setErrors({});
    setShowAddModal(true);
    
    // Load users for this company
    if (company.id && featureGroupId) {
      setLoadingUsers(true);
      try {
        const users = await getCompanyUsers(company.id, featureGroupId);
        setCompanyUsers(users);
      } catch (err) {
        logger.error('Error loading company users:', err);
        setCompanyUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    }
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company? This will remove all user associations with this company.')) {
      return;
    }

    try {
      await deleteCompany(companyId);
      await loadCompanies();
    } catch (err) {
      alert('Failed to delete company');
      logger.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!companyName.trim()) {
      newErrors.name = 'Company name is required';
    }
    if (!votesPerPoll || votesPerPoll < 1) {
      newErrors.votesPerPoll = 'Votes per poll must be at least 1';
    }
    if (companyWeight && (companyWeight < 1 || companyWeight > 100)) {
      newErrors.companyWeight = 'Company weight must be between 1 and 100';
    }
    if (buyFeatureFormEnabled) {
      if (!buyFeatureDimensionId || !buyFeatureTagId) {
        newErrors.buyFeature = 'Choose a tag domain (dimension) and tag for the voting form.';
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      const weight = companyWeight ? parseInt(companyWeight, 10) : 1;
      const adminId = companyAdminId || null;
      let savedCompanyId = editingCompany?.id || null;
      if (editingCompany) {
        await updateCompany(
          editingCompany.id,
          companyName.trim(),
          parseInt(votesPerPoll, 10),
          weight,
          companyType.trim() || null,
          adminId
        );
      } else {
        const created = await createCompany(
          companyName.trim(),
          featureGroupId,
          parseInt(votesPerPoll, 10),
          weight,
          companyType.trim() || null,
          adminId
        );
        savedCompanyId = created?.id || null;
      }

      if (savedCompanyId) {
        if (buyFeatureFormEnabled && buyFeatureDimensionId && buyFeatureTagId) {
          await updateCompanyBuyFeatureSettings(
            savedCompanyId,
            buyFeatureDimensionId,
            buyFeatureTagId
          );
        } else {
          await updateCompanyBuyFeatureSettings(savedCompanyId, null, null);
        }
      }

      setShowAddModal(false);
      await loadCompanies();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to save company' });
      logger.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setShowAddModal(false);
    setEditingCompany(null);
    setCompanyName('');
    setVotesPerPoll(1);
    setCompanyWeight('');
    setCompanyType('');
    setCompanyAdminId('');
    setCompanyUsers([]);
    setBuyFeatureFormEnabled(false);
    setBuyFeatureDimensionId('');
    setBuyFeatureTagId('');
    setTagsForDimension([]);
    setErrors({});
  };

  const copyBuyFeatureLink = (slug) => {
    if (!slug) return;
    const url = `${window.location.origin}/buy-feature/${slug}`;
    const showCopied = () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
      setCopiedBuyFeatureSlug(slug);
      copyFeedbackTimeoutRef.current = setTimeout(() => {
        setCopiedBuyFeatureSlug(null);
        copyFeedbackTimeoutRef.current = null;
      }, 2800);
    };
    navigator.clipboard.writeText(url).then(showCopied, () => {
      window.prompt('Copy this link:', url);
    });
  };

  const isCopiedSlug = (slug) => slug && copiedBuyFeatureSlug === slug;

  return (
    <div className="companies-container">
      <div className="companies-header">
        <h3>Companies</h3>
        <button className="add-company-button" onClick={handleAdd}>
          <i className="fas fa-plus"></i> Add Company
        </button>
      </div>

      <div className="companies-help-text">
        <i className="fas fa-info-circle"></i> Owners can assign users to Companies to allow larger groups of users to combine votes on features for the entire company. Use Buy-a-feature to give each company a private link to allocate up to 100 points across features tagged with a chosen dimension and tag (see{' '}
        <a href="https://www.5dvision.com/post/using-buy-a-feature/" target="_blank" rel="noreferrer">
          Buy-a-Feature
        </a>
        ).
      </div>

      {loading && <div className="loading">Loading companies...</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && (
        <div className="companies-list">
          {companies.length === 0 ? (
            <div className="no-companies">
              No companies yet. Click "Add Company" to create one.
            </div>
          ) : (
            <table className="companies-table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th># of Votes per Poll</th>
                  <th>Weight</th>
                  <th>Type</th>
                  <th>Admin</th>
                  <th>Buy-a-feature</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map(company => (
                  <tr key={company.id}>
                    <td className="company-name">{company.name}</td>
                    <td className="company-votes">{company.votes_per_poll || 1}</td>
                    <td className="company-weight">{company.company_weight || '—'}</td>
                    <td className="company-type">{company.company_type || '—'}</td>
                    <td className="company-admin">{company.company_admin_email || '—'}</td>
                    <td className="company-buy-feature">
                      {company.buy_feature_slug ? (
                        <button
                          type="button"
                          className={`copy-link-button${isCopiedSlug(company.buy_feature_slug) ? ' copy-link-button-copied' : ''}`}
                          onClick={() => copyBuyFeatureLink(company.buy_feature_slug)}
                          title="Copy voting link"
                        >
                          {isCopiedSlug(company.buy_feature_slug) ? (
                            <>
                              <i className="fas fa-check" aria-hidden />
                              Copied
                            </>
                          ) : (
                            <>
                              <i className="fas fa-link" aria-hidden />
                              Copy link
                            </>
                          )}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="company-actions">
                      <button
                        className="icon-button"
                        onClick={() => handleEdit(company)}
                        title="Edit Company"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="icon-button delete-button"
                        onClick={() => handleDelete(company.id)}
                        title="Delete Company"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={handleClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>{editingCompany ? 'Edit Company' : 'Add Company'}</h4>
              <button className="close-button" onClick={handleClose}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="companyName">
                  Company Name <span className="required">*</span>
                </label>
                <input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    setErrors({});
                  }}
                  placeholder="Enter company name"
                  className={errors.name ? 'error' : ''}
                  autoFocus
                />
                {errors.name && (
                  <span className="error-message">{errors.name}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="votesPerPoll">
                  # of Votes per Poll <span className="required">*</span>
                </label>
                <input
                  id="votesPerPoll"
                  type="number"
                  min="1"
                  value={votesPerPoll}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setVotesPerPoll(isNaN(value) ? '' : value);
                    setErrors({});
                  }}
                  placeholder="1"
                  className={errors.votesPerPoll ? 'error' : ''}
                />
                {errors.votesPerPoll && (
                  <span className="error-message">{errors.votesPerPoll}</span>
                )}
                <small className="help-text">
                  Number of votes this company gets per poll (minimum 1)
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="companyWeight">
                  Company Weight
                </label>
                <input
                  id="companyWeight"
                  type="number"
                  min="1"
                  max="100"
                  value={companyWeight}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setCompanyWeight(isNaN(value) ? '' : value);
                    setErrors({});
                  }}
                  placeholder="1-100"
                  className={errors.companyWeight ? 'error' : ''}
                />
                {errors.companyWeight && (
                  <span className="error-message">{errors.companyWeight}</span>
                )}
                <small className="help-text">
                  Adjust weight for Summary of Ratings across Companies (1-100, optional)
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="companyType">
                  Company Type
                </label>
                <input
                  id="companyType"
                  type="text"
                  value={companyType}
                  onChange={(e) => {
                    setCompanyType(e.target.value);
                    setErrors({});
                  }}
                  placeholder="Enter company type"
                />
                <small className="help-text">
                  Company type (optional)
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="companyAdmin">
                  Company Admin
                </label>
                {loadingUsers ? (
                  <div className="loading-text">Loading users...</div>
                ) : (
                  <select
                    id="companyAdmin"
                    value={companyAdminId}
                    onChange={(e) => {
                      setCompanyAdminId(e.target.value);
                      setErrors({});
                    }}
                  >
                    <option value="">No admin selected</option>
                    {companyUsers.map(user => (
                      <option key={user.user_id} value={user.user_id}>
                        {user.full_name} ({user.email})
                      </option>
                    ))}
                  </select>
                )}
                {companyUsers.length === 0 && editingCompany && !loadingUsers && (
                  <small className="help-text warning">
                    No users are currently linked to this company. Add users to the company first to select an admin.
                  </small>
                )}
                <small className="help-text">
                  Select a user from users linked to this company (optional)
                </small>
              </div>

              <div className="form-group companies-buy-feature-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={buyFeatureFormEnabled}
                    onChange={(e) => {
                      setBuyFeatureFormEnabled(e.target.checked);
                      setErrors({});
                      if (!e.target.checked) {
                        setBuyFeatureDimensionId('');
                        setBuyFeatureTagId('');
                        setTagsForDimension([]);
                      }
                    }}
                  />
                  <span>Enable Buy-a-feature voting link</span>
                </label>
                <small className="help-text">
                  Features that have the selected tag appear on this company&apos;s page. Participants allocate up to 100 points total (similar to{' '}
                  <a href="https://www.5dvision.com/post/using-buy-a-feature/" target="_blank" rel="noreferrer">
                    this method
                  </a>
                  ).
                </small>
                {buyFeatureFormEnabled && (
                  <>
                    <label htmlFor="buyFeatureDimension" className="sub-label">
                      Tag domain (dimension)
                    </label>
                    {loadingDimensions ? (
                      <div className="loading-text">Loading dimensions…</div>
                    ) : (
                      <select
                        id="buyFeatureDimension"
                        value={buyFeatureDimensionId}
                        onChange={(e) => {
                          setBuyFeatureDimensionId(e.target.value);
                          setBuyFeatureTagId('');
                          setErrors({});
                        }}
                      >
                        <option value="">Select dimension</option>
                        {dimensions.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <label htmlFor="buyFeatureTag" className="sub-label">
                      Tag name
                    </label>
                    {loadingTags ? (
                      <div className="loading-text">Loading tags…</div>
                    ) : (
                      <select
                        id="buyFeatureTag"
                        value={buyFeatureTagId}
                        onChange={(e) => {
                          setBuyFeatureTagId(e.target.value);
                          setErrors({});
                        }}
                        disabled={!buyFeatureDimensionId}
                      >
                        <option value="">Select tag</option>
                        {tagsForDimension.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {editingCompany?.buy_feature_slug && buyFeatureFormEnabled && (
                      <div className="buy-feature-url-preview">
                        <span className="buy-feature-url-label">Public URL</span>
                        <code className="buy-feature-url-code">
                          {`${window.location.origin}/buy-feature/${editingCompany.buy_feature_slug}`}
                        </code>
                        <button
                          type="button"
                          className={`copy-link-button inline${isCopiedSlug(editingCompany.buy_feature_slug) ? ' copy-link-button-copied' : ''}`}
                          onClick={() => copyBuyFeatureLink(editingCompany.buy_feature_slug)}
                        >
                          {isCopiedSlug(editingCompany.buy_feature_slug) ? (
                            <>
                              <i className="fas fa-check" aria-hidden />
                              Copied
                            </>
                          ) : (
                            'Copy'
                          )}
                        </button>
                      </div>
                    )}
                    {errors.buyFeature && (
                      <span className="error-message">{errors.buyFeature}</span>
                    )}
                  </>
                )}
              </div>

              {errors.submit && (
                <div className="error-message submit-error">{errors.submit}</div>
              )}
              <div className="modal-actions">
                <button type="button" className="cancel-button" onClick={handleClose}>
                  Cancel
                </button>
                <button type="submit" className="save-button" disabled={saving}>
                  {saving ? 'Saving...' : editingCompany ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;

